import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';

export type CategoryItem = {
  id: string;
  slug: string;
  name: string;
  parentId?: string | null;
  children?: CategoryItem[];
};

// The desktop nav is a horizontal hover menu. On mobile we render a different
// shape entirely (Hespress-style): a swipeable top-level category strip plus a
// hamburger that opens an off-canvas drawer with the full nested menu. We gate
// on a `.mobile` class toggled by matchMedia so the two never fight.
const MOBILE_QUERY = '(max-width: 991.98px)';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return isMobile;
}

export function Menu({ categories = [] }: { categories?: CategoryItem[] }) {
  const isMobile = useIsMobile();
  const tree = buildTree(categories);

  if (isMobile) return <MobileMenu tree={tree} />;
  return <DesktopMenu tree={tree} />;
}

function MobileMenu({ tree }: { tree: CategoryItem[] }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    const root = document.documentElement;
    if (open) root.style.overflow = 'hidden';
    else root.style.overflow = '';
    return () => {
      root.style.overflow = '';
    };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <nav className={`stellarnav mobile${open ? ' active' : ''}`}>
      <div className="m-bar">
        <ul className="m-strip">
          <li>
            <Link
              to="/"
              activeOptions={{ exact: true }}
              activeProps={{ className: 'active' }}
              aria-label="الرئيسية"
              onClick={close}
            >
              <i className="fas fa-home" />
            </Link>
          </li>
          {tree.map((cat) => (
            <li key={cat.id}>
              <Link
                to="/category/$"
                params={{ _splat: cat.slug }}
                search={{ page: undefined }}
                activeProps={{ className: 'active' }}
                onClick={close}
              >
                {cat.name}
              </Link>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="menu-toggle"
          aria-label="القائمة"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="bars">
            <span className="bar" /><span className="bar" /><span className="bar" />
          </span>
        </button>
      </div>

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className={`m-backdrop${open ? ' open' : ''}`} aria-hidden="true" onClick={close} />

      <aside className={`m-drawer${open ? ' open' : ''}`} aria-hidden={!open}>
        <div className="m-drawer-head">
          <span>الأقسام</span>
          <button type="button" className="m-drawer-close" aria-label="إغلاق القائمة" onClick={close}>
            <i className="fas fa-times" />
          </button>
        </div>
        <ul className="m-drawer-list">
          {tree.map((cat) => (
            <DrawerNode key={cat.id} cat={cat} onNavigate={close} />
          ))}
        </ul>
      </aside>
    </nav>
  );
}

function DrawerNode({ cat, onNavigate }: { cat: CategoryItem; onNavigate: () => void }) {
  const [open, setOpen] = useState(false);
  const hasChildren = !!cat.children?.length;

  return (
    <li className={`${hasChildren ? 'has-sub' : ''}${open ? ' open' : ''}`.trim() || undefined}>
      <Link
        to="/category/$"
        params={{ _splat: cat.slug }}
        search={{ page: undefined }}
        activeProps={{ className: 'active' }}
        onClick={onNavigate}
      >
        {cat.name}
      </Link>
      {hasChildren && (
        <button
          type="button"
          className="dd-toggle"
          aria-label="عرض الأقسام الفرعية"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} />
        </button>
      )}
      {hasChildren && (
        <ul className={`sub-menu${open ? ' open' : ''}`}>
          {cat.children!.map((c) => (
            <DrawerNode key={c.id} cat={c} onNavigate={onNavigate} />
          ))}
        </ul>
      )}
    </li>
  );
}

function DesktopMenu({ tree }: { tree: CategoryItem[] }) {
  return (
    <nav className="stellarnav desktop">
      <ul>
        <li>
          <Link to="/" activeOptions={{ exact: true }} activeProps={{ className: 'active' }} aria-label="الرئيسية">
            <i className="fas fa-home" />
          </Link>
        </li>
        {tree.map((cat) => (
          <DesktopNode key={cat.id} cat={cat} />
        ))}
      </ul>
    </nav>
  );
}

function DesktopNode({ cat }: { cat: CategoryItem }) {
  const [open, setOpen] = useState(false);
  const hasChildren = !!cat.children?.length;

  const hoverProps = hasChildren
    ? { onMouseEnter: () => setOpen(true), onMouseLeave: () => setOpen(false) }
    : {};

  return (
    <li className={hasChildren ? 'has-sub' : undefined} {...hoverProps}>
      <Link
        to="/category/$"
        params={{ _splat: cat.slug }}
        search={{ page: undefined }}
        activeProps={{ className: 'active' }}
      >
        {cat.name}
        <span />
      </Link>
      {hasChildren && (
        <ul className={`sub-menu${open ? ' open' : ''}`}>
          {cat.children!.map((c) => (
            <DesktopNode key={c.id} cat={c} />
          ))}
        </ul>
      )}
    </li>
  );
}

function buildTree(flat: CategoryItem[]): CategoryItem[] {
  const byId = new Map<string, CategoryItem>();
  flat.forEach((c) => byId.set(c.id, { ...c, children: [] }));
  const roots: CategoryItem[] = [];
  byId.forEach((c) => {
    if (c.parentId && byId.has(c.parentId)) {
      byId.get(c.parentId)!.children!.push(c);
    } else {
      roots.push(c);
    }
  });
  return roots;
}
